import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import Logo from "../../../../HomePage/img/logo.png";
import Photo from "../../../../HomePage/img/10276.jpg";

const CSS_AttrSelectors = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🔍 CSS Attribute Selectors</h1>

      <p className="tutorial-intro">
        CSS <b>attribute selectors</b> let you style elements based on the
        presence or value of their attributes. They are powerful when targeting
        links, forms, and UI components without extra classes or IDs.
      </p>

      {/* Why Learn Attr Selectors */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#1e3a8a", marginBottom: "0.5rem" }}>
          ✨ Why Use Attribute Selectors?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          They allow you to style elements dynamically based on attributes like{" "}
          <code>href</code>, <code>type</code>, <code>alt</code>, or{" "}
          <code>data-*</code>. This makes your CSS more flexible and reduces the
          need for extra classes.
        </p>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">
          👨‍💻 Example 1: Basic Attribute Selector
        </h2>
        <p>
          Selects all elements with a specific attribute, regardless of its
          value.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
a[target] {
  color: red;
}

/* HTML */
<a href="https://google.com" target="_blank">Google (new tab)</a>
<a href="https://openai.com">OpenAI (same tab)</a>`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <a href="https://google.com" target="_blank" style={{ color: "red" }}>
            Google (new tab)
          </a>{" "}
          |{" "}
          <a href="https://openai.com" style={{ color: "blue" }}>
            OpenAI (same tab)
          </a>
        </div>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Attribute Equals</h2>
        <p>Targets elements with an attribute equal to a specific value.</p>

        <CodeCopyBlock
          code={`/* CSS */
input[type="text"] {
  border: 2px solid green;
  padding: 5px;
}

/* HTML */
<input type="text" placeholder="Enter name" />
<input type="password" placeholder="Enter password" />`}
          language="css"
        />

        <div style={{ marginTop: "1rem", display: "flex", gap: "10px" }}>
          <input
            type="text"
            placeholder="Enter name"
            style={{ border: "2px solid green", padding: "5px" }}
          />
          <input
            type="password"
            placeholder="Enter password"
            style={{ border: "1px solid gray", padding: "5px" }}
          />
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Starts With (^=)</h2>
        <p>
          Selects elements whose attribute value <b>starts with</b> a given
          string.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
a[href^="https://"] {
  color: green;
}

/* HTML */
<a href="https://github.com">GitHub</a>
<a href="http://example.com">Example</a>`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <a href="https://github.com" style={{ color: "green" }}>
            GitHub
          </a>{" "}
          |{" "}
          <a href="http://example.com" style={{ color: "blue" }}>
            Example
          </a>
        </div>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: Ends With ($=)</h2>
        <p>Targets attributes whose values end with a specific string.</p>

        <CodeCopyBlock
          code={`/* CSS */
img[src$=".png"] {
  border: 3px solid orange;
  width: 60px;
  height: 60px;
}

/* HTML */
<img src="logo.png" alt="Logo" />
<img src="10276.jpg" alt="Photo" />`}
          language="css"
        />

        <div style={{ marginTop: "1rem", display: "flex", gap: "10px" }}>
          <img
            src={Logo}
            alt="Logo"
            style={{
              border: "3px solid orange",
              width: "60px",
              height: "60px",
            }}
          />
          <img
            src={Photo}
            alt="Photo"
            style={{ border: "1px solid gray", width: "60px", height: "60px" }}
          />
        </div>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Contains (*=)</h2>
        <p>
          Matches if the attribute value <b>contains</b> a specific substring.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
a[href*="openai"] {
  background-color: yellow;
  padding: 2px;
}

/* HTML */
<a href="https://openai.com">OpenAI</a>
<a href="https://example.com">Example</a>`}
          language="css"
        />

        <div style={{ marginTop: "1rem" }}>
          <a href="https://openai.com" style={{ backgroundColor: "yellow" }}>
            OpenAI
          </a>{" "}
          | <a href="https://example.com">Example</a>
        </div>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 6: Space (~=)</h2>
        <p>
          Matches if the attribute contains a word in a space-separated list.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
div[title~="box"] {
  border: 2px dashed purple;
  padding: 10px;
}

/* HTML */
<div title="red box">Red Box</div>
<div title="blue-circle">Blue Circle</div>`}
          language="css"
        />

        <div style={{ marginTop: "1rem", display: "flex", gap: "10px" }}>
          <div
            title="red box"
            style={{ border: "2px dashed purple", padding: "10px" }}
          >
            Red Box
          </div>
          <div
            title="blue-circle"
            style={{ border: "1px solid gray", padding: "10px" }}
          >
            Blue Circle
          </div>
        </div>
      </div>

      {/* Example 7 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 7: Hyphen (|=)</h2>
        <p>
          Matches if the attribute value is exactly equal or begins with the
          specified value followed by a hyphen.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
div[lang|="en"] {
  color: darkblue;
  font-weight: bold;
}

/* HTML */
<div lang="en">English</div>
<div lang="en-us">English (US)</div>
<div lang="fr">French</div>`}
          language="css"
        />

        <div style={{ marginTop: "1rem", display: "flex", gap: "15px" }}>
          <div lang="en" style={{ color: "darkblue", fontWeight: "bold" }}>
            English
          </div>
          <div lang="en-us" style={{ color: "darkblue", fontWeight: "bold" }}>
            English (US)
          </div>
          <div lang="fr" style={{ color: "black" }}>
            French
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Try combining attribute selectors with{" "}
          <a href="/css/pseudo-classes" className="learn-more">
            pseudo-classes
          </a>{" "}
          and{" "}
          <a href="/css/combinators" className="learn-more">
            combinators
          </a>{" "}
          to build powerful, dynamic styles without extra markup.
        </p>
      </div>
    </div>
  );
};

export default CSS_AttrSelectors;
